import { Article } from '../../models/sizing-data';
import { SizingStore } from './sizing-store';
import presizeBackend from '../../services/presize-backend';
import presizeBackendRest from '../../services/presize-backend-rest';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import { sendEvent } from '../../services/event-tracking';
import { sendSizingAppliedEvent } from '../../lib/fragment-helper';
import { DistributionType } from '../../models/sizing';
import { mapRestDistributionToStore } from '../../lib/adapters/distribution-adapter';

export class ResetArticleQuantitiesAction {
  async resetArticleQuantities(this: SizingStore, article: Article): Promise<void> {
    const group = this.groupByArticleRefId[article.referenceId];
    if (!isDbMigrated()) {
      await presizeBackend.resetEditedQuantities(this.referenceId, article.referenceId);
    } else if (
      group.selectedSizeKey?.distributionType === DistributionType.MANUAL &&
      group.selectedSizeChart?.sizeChartCode &&
      group.selectedSizeKey?.sizeKeyId
    ) {
      // Use cached recommended distribution when available and still valid for the current
      // chart/key selection, to avoid an extra API call.
      // Fall back to fetching from the backend if the cache is empty or stale.
      const cached = group.recommendedDistribution;
      const cacheIsValid =
        cached &&
        cached.sizeChartCode === group.selectedSizeChart.sizeChartCode &&
        cached.lengthChartCode === (group.selectedSizeChart.lengthChartCode ?? null) &&
        cached.sizeKeyId === group.selectedSizeKey.sizeKeyId &&
        (cached.referenceBrandCode ?? null) === (group.referenceBrandCode ?? null);
      if (cacheIsValid) {
        group.selectedSizeKey.distribution = cached.distribution;
        group.selectedSizeKey.distributionType = cached.distributionType;
        group.selectedSizeKey.recommendationLevel = cached.recoLevel;
        group.selectedSizeKey.recommendationStrategy = cached.recoStrategy;
      } else {
        const restDist = await presizeBackendRest.getSizeDistribution({
          cg_code: group.commodityGroupCode,
          brand_code: this.brandCode,
          season_code: this.seasonCode,
          size_structure: group.sizeStructure,
          size_chart_code: group.selectedSizeChart.sizeChartCode,
          length_chart_code: group.selectedSizeChart.lengthChartCode ?? null,
          size_keys: group.selectedSizeKey.sizeKeyId,
          silo: group.silo ?? null,
        });
        const mapped = mapRestDistributionToStore(restDist);
        if (mapped) {
          group.selectedSizeKey.distribution = mapped.distribution;
          group.selectedSizeKey.distributionType = mapped.distributionType;
          group.selectedSizeKey.recommendationLevel = mapped.recoLevel;
          group.selectedSizeKey.recommendationStrategy = mapped.recoStrategy;
          // Backfill cache so subsequent resets are instant.
          group.recommendedDistribution = {
            ...mapped,
            sizeChartCode: group.selectedSizeChart.sizeChartCode,
            lengthChartCode: group.selectedSizeChart.lengthChartCode ?? null,
            sizeKeyId: group.selectedSizeKey.sizeKeyId,
            referenceBrandCode: group.referenceBrandCode ?? null,
          };
        }
      }
    }
    article.editedQuantities = undefined;
    await this.calculateQuantities(false, group, [article.referenceId], undefined, true);
    sendSizingAppliedEvent(group, [article]);
    sendEvent(group, 'changeArticleSizing', [article], this.additionalData());
  }
}
